import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null);
  const { applyServerTheme } = useTheme();

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (data?.session_expires_at) setSessionExpiresAt(data.session_expires_at);
      if (data?.app_theme) applyServerTheme(data.app_theme);
      return data;
    } catch (e) {
      setUser(null);
      return null;
    }
  }, [applyServerTheme]);

  const checkAuth = useCallback(async () => {
    setLoading(true);
    await refresh();
    setLoading(false);
  }, [refresh]);

  useEffect(() => {
    // CRITICAL: returning from OAuth callback -> let AuthCallback exchange session_id first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Sliding session refresh: while logged in AND the tab is visible, ping the
  // backend every 8 min so the session never expires mid-work (long imports, etc.).
  useEffect(() => {
    if (!user?.user_id) return;
    const ping = () => {
      if (document.visibilityState === "visible")
        api.post("/auth/heartbeat")
          .then(({ data }) => { if (data?.expires_at) setSessionExpiresAt(data.expires_at); })
          .catch(() => {});
    };
    ping();
    const timer = setInterval(ping, 8 * 60 * 1000);
    const onVis = () => { if (document.visibilityState === "visible") ping(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", onVis); };
  }, [user?.user_id]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) {}
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, refresh, logout, checkAuth, sessionExpiresAt }}>
      {children}
    </AuthContext.Provider>
  );
};
